import api from "./api";

export interface Supplier {
  _id: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  taxId?: string;
  paymentTerms: "net_30" | "net_60" | "cash_on_delivery" | "advance_payment";
  creditLimit: number;
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSupplierData {
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  taxId?: string;
  paymentTerms?: "net_30" | "net_60" | "cash_on_delivery" | "advance_payment";
  creditLimit?: number;
  notes?: string;
}

export interface SuppliersResponse {
  success: boolean;
  data: Supplier[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalSuppliers: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface SupplierResponse {
  success: boolean;
  data: Supplier;
  message?: string;
}

class SupplierService {
  async getSuppliers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
  }): Promise<SuppliersResponse> {
    const response = await api.get("/suppliers", { params });
    return response.data;
  }

  async getSupplier(id: string): Promise<SupplierResponse> {
    const response = await api.get(`/suppliers/${id}`);
    return response.data;
  }

  async createSupplier(
    supplierData: CreateSupplierData
  ): Promise<SupplierResponse> {
    const response = await api.post("/suppliers", supplierData);
    return response.data;
  }

  async updateSupplier(
    id: string,
    supplierData: Partial<CreateSupplierData>
  ): Promise<SupplierResponse> {
    const response = await api.put(`/suppliers/${id}`, supplierData);
    return response.data;
  }

  async deleteSupplier(
    id: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/suppliers/${id}`);
    return response.data;
  }

  async getSupplierStats(): Promise<{
    success: boolean;
    data: {
      totalSuppliers: number;
      activeSuppliers: number;
      inactiveSuppliers: number;
      paymentTermsBreakdown: Array<{ _id: string; count: number }>;
    };
  }> {
    const response = await api.get("/suppliers/stats");
    return response.data;
  }
}

export default new SupplierService();
